import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import { Add, Storage, TableChart } from '@mui/icons-material';
import MainCard from 'components/MainCard';
import ResourceTypeManager from './components/ResourceTypeManager';
import ClientFilter from 'components/ClientFilter';
import ResourceDataManager from './components/ResourceDataManager';
import UniversalResourceSearch from './components/UniversalResourceSearch';
import useSelection from 'hooks/useSelection';
import useClientAutoSelector from 'hooks/useClientAutoSelector';
import useLegalAccess from 'hooks/useLegalAccess';

interface Field {
  name: string;
  type: 'text' | 'number' | 'email' | 'date' | 'textarea' | 'dropdown' | 'image' | 'license_verification';
  required: boolean;
  dropdownSource?: 'teams' | 'countryCode';
  dropdownOptions?: string[];
} 

interface ResourceType {
  id: string;
  name: string;
  description: string;
  fields: Field[];
  createdAt: string;
  clientUID?: string;
  category?: 'user' | 'others';
  access?: string[];
  invite?: boolean;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`resources-tabpanel-${index}`}
      aria-labelledby={`resources-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

// Distinct from "" so the filter can tell "nothing picked yet" (the first render,
// which must keep showing the pick-a-client prompt) apart from "All picked".
const ALL_CLIENTS = '__all_clients__';

const Resources = () => {
  const [tabValue, setTabValue] = useState(0);
  const [selectedResourceType, setSelectedResourceType] = useState<ResourceType | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  
  const { selectedClient, setSelectedClient, clientOptions } = useSelection();

  // Super Admin (server-decided, `!role.client` — never a role-name match). Only
  // they get the "All" client option, because only their resources span clients.
  const { isSuperAdmin } = useLegalAccess();

  // "All" is a view of this page, not a change of the app-wide client selection:
  // `selectedClient` is shared with transactions/dashboard, so blanking it there
  // would silently unscope those pages too. Kept local and layered on top.
  const [allClients, setAllClients] = useState(false);

  // What the tabs actually query with. `null` means "every client" server-side.
  const effectiveClientUID = allClients ? null : selectedClient;

  // Resource types and resource data are created against one client, so "All" is
  // a read-only view — the Types and Add tabs stay behind a concrete client.
  const canManage = !allClients;

  // Auto-select client if there's only one available (important for users without dashboard access)
  useClientAutoSelector();

  // Reset state when the effective scope changes — including into and out of "All".
  useEffect(() => {
    setSelectedResourceType(null);
    setRefreshTrigger(prev => prev + 1);
  }, [selectedClient, allClients]);

  // Leaving Super Admin (or losing the flag while it loads) must not strand the
  // page in a scope that is no longer offered. !
  useEffect(() => {
    if (!isSuperAdmin && allClients) setAllClients(false);
  }, [isSuperAdmin, allClients]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const handleResourceTypeSelect = (resourceType: ResourceType | null) => {
    setSelectedResourceType(resourceType);
  };

  // Show validation message if no client selected
  const isClientSelected = allClients || Boolean(selectedClient);

  return (
    <MainCard title="Resources Directory">
      <Box sx={{ width: '100%' }}>
        {/* Header Section with Client Filter */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 3 }}>
          <Box sx={{ flex: 1, minWidth: '300px' }}>
            <Typography variant="h4" gutterBottom>
              Resource Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage resource types, define their fields, and store resource data for your organization.
            </Typography>
          </Box>
          
          {/* Client Selection Section - Only show when there are 2 or more clients */}
          {clientOptions.length >= 2 && (
            <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, minWidth: '320px' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Client Filter:
                </Typography>
                <ClientFilter
                  clientOptions={clientOptions}
                  selectedClient={allClients ? ALL_CLIENTS : selectedClient}
                  showAll={isSuperAdmin}
                  allValue={ALL_CLIENTS}
                  onChange={(clientId) => {
                    const pickedAll = clientId === ALL_CLIENTS;
                    setAllClients(pickedAll);
                    if (!pickedAll) setSelectedClient(clientId);
                    setRefreshTrigger(prev => prev + 1);
                    setSelectedResourceType(null);
                    // All is read-only, so land on View Resources rather than a
                    // Types tab that cannot create anything.
                    setTabValue(pickedAll ? 2 : 0);
                  }}
                  size="small"
                  sx={{ minWidth: 250 }}
                  label="Select Client"
                />
              </Box>
              {!isClientSelected && (
                <Typography variant="body2" color="warning.main" sx={{ fontStyle: 'italic', mt: 1 }}>
                  ⚠️ Please select a client to view and manage resources
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {/* Navigation Tabs - Show when client is selected OR when there's only one client */}
        {isClientSelected || (clientOptions.length === 1) ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={tabValue} onChange={handleTabChange} aria-label="resources tabs">
                {/* Both create against one client, so they are off in "All". */}
                <Tab
                  icon={<Storage />}
                  label="Resource Types"
                  id="resources-tab-0"
                  aria-controls="resources-tabpanel-0"
                  disabled={!canManage}
                />
                <Tab
                  icon={<Add />}
                  label="Add Resource"
                  id="resources-tab-1"
                  aria-controls="resources-tabpanel-1"
                  disabled={!canManage}
                />
                <Tab 
                  icon={<TableChart />} 
                  label="View Resources" 
                  id="resources-tab-2"
                  aria-controls="resources-tabpanel-2"
                />
              </Tabs>
            </Box>

            {/* Tab Panels */}
            <TabPanel value={tabValue} index={0} key={`type-manager-${effectiveClientUID ?? "all"}`}>
              <ResourceTypeManager 
                onRefresh={refreshData}
                refreshTrigger={refreshTrigger}
                onResourceTypeSelect={handleResourceTypeSelect}
                clientUID={effectiveClientUID}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={1} key={`data-manager-${effectiveClientUID ?? "all"}`}>
              <ResourceDataManager 
                selectedResourceType={selectedResourceType}
                onRefresh={refreshData}
                onTabChange={() => setTabValue(0)}
                clientUID={effectiveClientUID}
              />
            </TabPanel>

            <TabPanel value={tabValue} index={2} key={`search-manager-${effectiveClientUID ?? "all"}`}>
              <UniversalResourceSearch clientUID={effectiveClientUID} />
              {/* <ResourceTableView 
                refreshTrigger={refreshTrigger}
                selectedResourceType={selectedResourceType}
                onResourceTypeSelect={handleResourceTypeSelect}
                clientUID={effectiveClientUID}
              /> */}
            </TabPanel>
          </>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8, bgcolor: 'grey.50', borderRadius: 2 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {clientOptions.length === 0 ? 'No Clients Available' : 'Client Selection Required'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {clientOptions.length === 0 
                ? 'No clients found. Please contact your administrator to set up client access.'
                : 'Please select a client from the filter above to access resource management features. This ensures resources are properly associated with the correct client.'
              }
            </Typography>
          </Box>
        )}
      </Box>
    </MainCard>
  );
};

export default Resources;
